// "Live store changes" in the Store package modal: what the daily watch of the
// public Play page found for this app, per locale, with before/after. The
// package above is what we handed over; this is what the store actually shows.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { checkListingNow, getListingChanges, type ListingChange, type ListingField, type ListingValues } from '@/api/listing-changes';
import { formatDateTime } from '@/lib/datetime';
import { useCanEdit } from '@/components/EditOnly';

const FIELD_LABELS: Record<ListingField, string> = {
  title: 'Title',
  short_description: 'Short description',
  full_description: 'Full description',
  icon: 'Icon',
  feature: 'Feature graphic',
  screenshots: 'Screenshots',
  video: 'Video',
  developer: 'Developer',
};

const IMAGE_FIELDS: ListingField[] = ['icon', 'feature'];

function fromEpoch(s: number | null | undefined): string {
  return s ? formatDateTime(new Date(s * 1000).toISOString()) : '—';
}

/** play-lh identities are stored without a size; ask for a thumbnail. */
function thumb(identity: string): string {
  return identity.includes('googleusercontent.com') ? `${identity}=w160` : identity;
}

function Value({ field, v }: { field: ListingField; v: ListingValues }) {
  if (field === 'screenshots') {
    const list = v.screenshots ?? [];
    if (list.length === 0) return <span className="text-content-subtle">none</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {list.map((s, i) => (
          <a key={`${s}-${i}`} href={thumb(s).replace(/=w160$/, '=w1200')} target="_blank" rel="noreferrer">
            <img src={thumb(s)} alt="" className="h-16 rounded border border-border object-cover" />
          </a>
        ))}
      </div>
    );
  }
  if (IMAGE_FIELDS.includes(field)) {
    const s = (v[field] as string | undefined) ?? '';
    return s
      ? <img src={thumb(s)} alt="" className="h-12 rounded border border-border object-cover" />
      : <span className="text-content-subtle">none</span>;
  }
  const s = String(v[field] ?? '');
  if (!s) return <span className="text-content-subtle">empty</span>;
  return (
    <p className={`break-words text-content ${field === 'full_description' ? 'max-h-40 overflow-y-auto whitespace-pre-wrap text-xs leading-5' : ''}`}>
      {s}
    </p>
  );
}

function ChangeRow({ change }: { change: ListingChange }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-b border-border last:border-b-0">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-surface-muted">
        <span className="w-36 shrink-0 text-xs text-content-subtle">{fromEpoch(change.detected_at)}</span>
        <span className="w-14 shrink-0 font-mono text-xs text-content-muted">{change.lang}</span>
        <span className="flex-1 text-content">{change.fields.map((f) => FIELD_LABELS[f] ?? f).join(', ')}</span>
        <span className="text-xs text-content-subtle">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="space-y-3 px-3 pb-3">
          {change.fields.map((f) => (
            <div key={f} className="rounded-md border border-border">
              <p className="border-b border-border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-subtle">{FIELD_LABELS[f] ?? f}</p>
              <div className="grid gap-3 px-3 py-2 text-sm sm:grid-cols-2">
                <div>
                  <p className="mb-1 text-[11px] text-content-subtle">Before</p>
                  <Value field={f} v={change.before} />
                </div>
                <div>
                  <p className="mb-1 text-[11px] text-content-subtle">After</p>
                  <Value field={f} v={change.after} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

export function ListingChangesSection({ appId }: { appId: string }) {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const watch = useQuery({ queryKey: ['listing-changes', appId], queryFn: () => getListingChanges(appId), enabled: !!appId });
  const check = useMutation({
    mutationFn: () => checkListingNow(appId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listing-changes', appId] }),
  });

  const locales = watch.data?.locales ?? [];
  const changes = watch.data?.changes ?? [];

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-content-subtle">Live store changes</p>
        {canEdit && (
          <button type="button" onClick={() => check.mutate()} disabled={check.isPending} className="text-xs text-primary hover:underline disabled:opacity-50">
            {check.isPending ? 'Reading the store…' : 'Check now'}
          </button>
        )}
      </div>
      {watch.isLoading ? (
        <p className="text-sm text-content-subtle">Loading…</p>
      ) : watch.error ? (
        <p className="text-sm text-danger">{watch.error instanceof Error ? watch.error.message : 'Failed to load'}</p>
      ) : (
        <div className="space-y-2">
          {locales.length === 0 ? (
            <p className="text-sm text-content-subtle">Not read yet — the store is read once a day for live Android apps.</p>
          ) : (
            <p className="text-xs text-content-subtle">
              {locales.map((l) => (
                <span key={l.lang} className="mr-3 inline-block" title={l.geo ? `Read with gl=${l.geo}` : undefined}>
                  <span className="font-mono">{l.lang}</span>{' '}
                  {l.last_error
                    ? <span className="text-warning">{l.last_error === 'not_found' ? 'not visible' : 'error'}{l.checked_at ? `, last read ${fromEpoch(l.checked_at)}` : ''}</span>
                    : <>read {fromEpoch(l.checked_at)}</>}
                </span>
              ))}
            </p>
          )}
          {check.isError && (
            <p className="text-xs text-danger">{check.error instanceof Error ? check.error.message : 'Check failed'}</p>
          )}
          {changes.length === 0 ? (
            <p className="text-sm text-content-subtle">No changes seen.</p>
          ) : (
            <ul className="rounded-lg border border-border bg-surface">
              {changes.map((c) => <ChangeRow key={c.id} change={c} />)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
